"use client";

import type { WorkspaceFileItem } from "@/app/api/__generated__/models/workspaceFileItem";
import { Icon } from "@/components/atoms/Icon/Icon";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/molecules/DropdownMenu/DropdownMenu";
import { cn } from "@/lib/utils";
import {
  Delete02Icon,
  Download04Icon,
  Folder01Icon,
  LinkSquare01Icon,
  Loading03Icon,
  MoreHorizontalIcon,
  PencilEdit02Icon,
} from "@hugeicons/core-free-icons";
import Link from "next/link";
import { MoveToFolderDialog } from "../MoveToFolderDialog/MoveToFolderDialog";
import { RenameFileDialog } from "../RenameFileDialog/RenameFileDialog";
import { useFileActionsMenu } from "./useFileActionsMenu";

interface Props {
  file: WorkspaceFileItem;
  className?: string;
}

export function FileActionsMenu({ file, className }: Props) {
  const {
    goLabel,
    goHref,
    isMoveOpen,
    setIsMoveOpen,
    isRenameOpen,
    setIsRenameOpen,
    isDownloading,
    isDeleting,
    handleDownload,
    handleDelete,
  } = useFileActionsMenu(file);

  return (
    <>
      <DropdownMenu data-sentry-element="DropdownMenu" data-sentry-source-file="FileActionsMenu.tsx">
        <DropdownMenuTrigger asChild data-sentry-element="DropdownMenuTrigger" data-sentry-source-file="FileActionsMenu.tsx">
          <button
            type="button"
            aria-label={`Actions for ${file.name}`}
            className={cn(
              "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-foreground-tertiary transition-colors hover:bg-surface-hover hover:text-foreground-strong",
              className,
            )}
            data-testid="artifacts-card-menu"
          >
            <Icon icon={MoreHorizontalIcon} size={20} data-sentry-element="Icon" data-sentry-source-file="FileActionsMenu.tsx" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44" data-sentry-element="DropdownMenuContent" data-sentry-source-file="FileActionsMenu.tsx">
          <DropdownMenuItem
            onSelect={handleDownload}
            disabled={isDownloading}
            data-testid="artifacts-download"
          data-sentry-element="DropdownMenuItem" data-sentry-source-file="FileActionsMenu.tsx">
            {isDownloading ? (
              <span className="mr-2 flex animate-spin">
                <Icon icon={Loading03Icon} size={16} />
              </span>
            ) : (
              <Icon icon={Download04Icon} size={16} className="mr-2" />
            )}
            {isDownloading ? "Downloading…" : "Download"}
          </DropdownMenuItem>
          <DropdownMenuItem asChild data-sentry-element="DropdownMenuItem" data-sentry-source-file="FileActionsMenu.tsx">
            <Link href={goHref} data-testid="artifacts-origin-link" data-sentry-element="Link" data-sentry-source-file="FileActionsMenu.tsx">
              <Icon icon={LinkSquare01Icon} size={16} className="mr-2" data-sentry-element="Icon" data-sentry-source-file="FileActionsMenu.tsx" />
              {goLabel}
            </Link>
          </DropdownMenuItem>
          {/* preventDefault keeps the menu mounted while the dialog opens, so
              the menu's focus return doesn't fight the dialog for focus. */}
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              setIsRenameOpen(true);
            }}
            data-testid="artifacts-rename-menu"
          data-sentry-element="DropdownMenuItem" data-sentry-source-file="FileActionsMenu.tsx">
            <Icon icon={PencilEdit02Icon} size={16} className="mr-2" data-sentry-element="Icon" data-sentry-source-file="FileActionsMenu.tsx" />
            Rename
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              setIsMoveOpen(true);
            }}
            data-testid="artifacts-move-to-folder"
          data-sentry-element="DropdownMenuItem" data-sentry-source-file="FileActionsMenu.tsx">
            <Icon icon={Folder01Icon} size={16} className="mr-2" data-sentry-element="Icon" data-sentry-source-file="FileActionsMenu.tsx" />
            Move to folder
          </DropdownMenuItem>
          <DropdownMenuSeparator data-sentry-element="DropdownMenuSeparator" data-sentry-source-file="FileActionsMenu.tsx" />
          <DropdownMenuItem
            onSelect={handleDelete}
            disabled={isDeleting}
            // oxlint-disable-next-line design-system/no-restyle -- a destructive item; the menu molecule has no destructive variant
            className="text-error-hover focus:bg-error-surface focus:text-error-foreground"
            data-testid="artifacts-delete"
          data-sentry-element="DropdownMenuItem" data-sentry-source-file="FileActionsMenu.tsx">
            {isDeleting ? (
              <span className="mr-2 flex animate-spin">
                <Icon icon={Loading03Icon} size={16} />
              </span>
            ) : (
              <Icon icon={Delete02Icon} size={16} className="mr-2" />
            )}
            {isDeleting ? "Deleting…" : "Delete"}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {isRenameOpen && (
        <RenameFileDialog
          file={file}
          isOpen={isRenameOpen}
          setIsOpen={setIsRenameOpen}
        />
      )}
      {isMoveOpen && (
        <MoveToFolderDialog
          move={{
            kind: "files",
            fileIds: [file.id],
            currentFolderId: file.folder_id,
          }}
          subject={`“${file.name}”`}
          isOpen={isMoveOpen}
          setIsOpen={setIsMoveOpen}
        />
      )}
    </>
  );
}
